<template>
  <button class="ai-header-button"
          tabindex="0"
          type="button"
          aria-label="Ask Expert AI"
          :class="{ 'visible-search-bar': searchBarHeader,
                    'visible-login-register-buttons': loginRegisterButtons,
                    'is-mobile': isMobile,
                    'is-cobranding': showCoBranding }"
          @click="handleAction">
    <div class="ai-header-button-content">
      <div class="ai-header-icon-container">
        <img class="ai-header-icon" alt="ai-header-icon">
      </div>
      <div class="ai-header-button-text-container">
        <div class="ai-header-button-text text-normal">
          {{ $t("HEADER.ASK_EXPERT_AI") }}
        </div>
        <div class="ai-header-button-text text-small">
          {{ $t("HEADER.ASK_EXPERT_AI_SHORT") }}
        </div>
      </div>
    </div>
  </button>
  <LoginRegisterModal ref="loginRegisterModal" />
</template>

<script setup>
import { useStore } from 'vuex';
import { computed, ref } from 'vue';
import LoginRegisterModal from '_acaSrc/components/login/LoginRegisterModal.vue';
import useExpertAiAction from '_acaSrc/composables/useExpertAiAction.composable';

defineProps({
    searchBarHeader: {
        type: Boolean,
        default: false
    },
    loginRegisterButtons: {
        type: Boolean,
        default: false
    },
    isMobile: {
        type: Boolean,
        default: false
    }
});

const store = useStore();
const showCoBranding = computed(() => store.getters['app/showCoBranding']);
const loginRegisterModal = ref(null);
const { handleExpertAiAction } = useExpertAiAction();

const handleAction = async(event) => {
    const uiElementName = 'ai-header-btn';
    await handleExpertAiAction({
        event,
        uiElementName,
        loginRegisterModalRef: loginRegisterModal
    });
};
</script>

<style lang="less">
@import (reference) '~_acaAssets/wkce/wkce-app-spacing.less';
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.ai-header-button {
  display: inline-flex;
  height: 32px;
  align-items: flex-start;
  margin-top: 12px;
  border: none;
  outline: none;
  background: none;
  padding: 0;

  .ai-header-button-content {
    display: flex;
    padding: 6px;
    align-items: center;
    align-self: center;
    border-radius: 50%;
    border-width: 1px;
    border-style: solid;
    border-color: @WKCE-CREAM-TINT1;

    .ai-header-icon-container {
      display: flex;
      align-items: center;

      .ai-header-icon {
        content: url(/utd-ai/utd-ai-spark-mobile.svg);
      }
    }

    .ai-header-button-text-container {
      display: flex;
      align-items: flex-start;

      .ai-header-button-text {
        color: @WKCE-WHITE;
        font-variant-numeric: lining-nums proportional-nums;
        font-feature-settings: 'case' on, 'cpsp' on, 'dlig' on, 'mgrk' on, 'liga' off, 'clig' off;
        font-family: "Fira Sans", sans-serif;
        font-size: 18px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px;

        &.text-normal,
        &.text-small {
          display: none;
        }
      }
    }
  }

  &:not(.visible-login-register-buttons) {
    .ds1-ml-1()
  }

  &.is-mobile.visible-search-bar {
    .ds1-ml-0();
    position: absolute;
    left: 44px;
  }

  &:focus-visible {
    .ai-header-button-content {
      border-width: 2px;
      border-style: solid;
      border-color: @WKCE-WHITE;
    }
  }

  &:hover {
    .ai-header-button-content {
      background-color: @WKCE-BLUE-SHADE1;
    }
  }

  &:active {
    .ai-header-button-content {
      background-color: @WKCE-BLUE;
    }
  }
}

@media (min-width: 430px) {
  .ai-header-button {
    .ai-header-button-content {
      border-radius: 16px;
      padding: 8px 11px;
      align-self: stretch;
      gap: 8px;

      .ai-header-button-text-container .ai-header-button-text {
        &.text-normal {
          display: block;
        }

        &.text-small {
          display: none;
        }
      }
    }

    &:not(.visible-login-register-buttons) {
      .ds1-ml-2();
    }

    &.visible-search-bar {
      .ai-header-button-content {
        border-radius: 50%;
        padding: 6px;
        align-items: center;
        align-self: center;
        gap: 0;

        .ai-header-button-text-container .ai-header-button-text {
          &.text-normal,
          &.text-small {
            display: none;
          }
        }
      }
    }
  }
}

@media (min-width: 768px) {
  .ai-header-button {
    &.visible-login-register-buttons {
      margin-top: 7px;
    }

    .ai-header-button-content .ai-header-button-text-container .ai-header-button-text {
      &.text-normal {
        display: block;
      }

      &.text-small {
        display: none;
      }
    }
  }

  .isDesktop {
    .ai-header-button {
      .ds1-ml-2();
      margin-top: 10px;
      height: 34px;

      .ai-header-button-content {
        border-radius: 16px;
        padding: 8px 11px;
        align-self: stretch;
        gap: 8px;

        .ai-header-icon-container .ai-header-icon {
          content: url(/utd-ai/utd-ai-spark.svg);
        }

        .ai-header-button-text-container .ai-header-button-text {
          color: @WKCE-GRAY-SHADE2;

          &.text-normal {
            display: block;
          }

          &.text-small {
            display: none;
          }
        }
      }

      &.visible-search-bar .ai-header-button-content .ai-header-button-text-container {
        .ai-header-button-text.text-normal {
          display: none;
        }

        .ai-header-button-text.text-small {
          display: block;
        }
      }

      &:not(.visible-search-bar).is-cobranding .ai-header-button-content {
        .ai-header-button-text-container {
          .ai-header-button-text.text-normal {
            display: none;
          }

          .ai-header-button-text.text-small {
            display: block;
          }
        }
      }

      &:focus-visible {
        .ai-header-button-content {
          border-width: 2px;
          border-style: solid;
          border-color: @WKCE-BLACK;
        }
      }

      &:hover {
        .ai-header-button-content {
          background-color: @WKCE-CREAM-TINT3;
        }
      }

      &:active {
        .ai-header-button-content {
          background-color: @WKCE-CREAM-TINT1;
        }
      }
    }
  }
}

@media (min-width: 1069px) {
  .isDesktop {
    .ai-header-button {
      .ds1-ml-4();

      &:not(.visible-search-bar).is-cobranding .ai-header-button-content {
        .ai-header-button-text-container {
          .ai-header-button-text.text-normal {
            display: block;
          }

          .ai-header-button-text.text-small {
            display: none;
          }
        }
      }
    }
  }
}

@media (min-width: 1440px) {
  .isDesktop {
    .ai-header-button {
      .text-normal {
        display: block;
      }

      .text-small {
        display: none;
      }

      &.visible-search-bar .ai-header-button-content .ai-header-button-text-container{
        .ai-header-button-text.text-normal {
          display: block;
        }

        .ai-header-button-text.text-small {
          display: none;
        }
      }
    }
  }
}
</style>